import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Clock,
  DollarSign,
  MapPin,
  Users,
  ArrowLeft,
  Share2,
  Star,
  Heart
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const PlanDetails = () => {
  const { planId } = useParams();
  const [plan, setPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPlanDetails = useCallback(async () => {
    try {
      const response = await axios.get(`/api/plans/${planId}`);
      if (response.data.success) {
        setPlan(response.data.plan);
      }
    } catch (error) {
      console.error('Fetch plan error:', error);
      toast.error('Failed to load plan details');
    } finally {
      setIsLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    fetchPlanDetails();
  }, [fetchPlanDetails]);

  const sharePlan = () => {
    const planUrl = `${window.location.origin}/plan/${planId}`;
    navigator.clipboard.writeText(planUrl);
    toast.success('Plan link copied to clipboard!');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading plan...</p>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Plan Not Found</h2>
        <p className="text-gray-600">The plan you're looking for doesn't exist.</p>
        <Link to="/" className="btn-primary mt-4 inline-block">
          Go Home
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <Link to="/" className="btn-secondary flex items-center space-x-2">
            <ArrowLeft size={16} />
            <span>Back</span>
          </Link>
          <button
            onClick={sharePlan}
            className="btn-secondary flex items-center space-x-2"
          >
            <Share2 size={16} />
            <span>Share</span>
          </button>
        </div>

        {/* Plan Header */}
        <div className="card">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {plan.title}
            </h1>
            <p className="text-lg text-gray-600">{plan.description}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="flex items-center space-x-2">
              <DollarSign size={20} className="text-green-600" />
              <div>
                <div className="text-sm text-gray-500">Cost</div>
                <div className="font-semibold">{plan.estimatedCost}</div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Clock size={20} className="text-blue-600" />
              <div>
                <div className="text-sm text-gray-500">Duration</div>
                <div className="font-semibold">{plan.duration}</div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Users size={20} className="text-purple-600" />
              <div>
                <div className="text-sm text-gray-500">Group Size</div>
                <div className="font-semibold">{plan.groupSize} people</div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <MapPin size={20} className="text-red-600" />
              <div>
                <div className="text-sm text-gray-500">Location</div>
                <div className="font-semibold">Rutgers University</div>
              </div>
            </div>
          </div>

          {/* Weather & Events */}
          {plan.weather && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-900 mb-2">Weather & Conditions</h3>
              <div className="flex items-center justify-between">
                <span>{plan.weather.condition}, {plan.weather.temperature}</span>
                <span className="text-sm text-blue-600">{plan.weather.recommendation}</span>
              </div>
            </div>
          )}

          {plan.localEvents && plan.localEvents.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-900 mb-2">Local Events</h3>
              <ul className="space-y-1">
                {plan.localEvents.map((event, index) => (
                  <li key={index} className="text-sm text-purple-700">• {event}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Itinerary */}
        <div className="card">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Itinerary</h2>
          
          <div className="space-y-4">
            {plan.itinerary?.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * index }}
                className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg"
              >
                <div className="flex-shrink-0 w-16 text-center">
                  <div className="text-sm font-semibold text-gray-900">{item.time}</div>
                  <div className="text-xs text-gray-500">{item.activity}</div>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">{item.venue}</h3>
                  <p className="text-gray-600 text-sm mb-2">{item.description}</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{item.location}</span>
                    <span className="font-semibold text-green-600">{item.cost}</span>
                  </div>
                  {item.travelTime && (
                    <div className="mt-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                      🚶 {item.travelTime}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Student Perks */}
        {plan.studentPerks && plan.studentPerks.length > 0 && (
          <div className="card bg-green-50 border-green-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Student Perks & Discounts</h2>
            <ul className="space-y-3">
              {plan.studentPerks.map((perk, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="flex items-start space-x-3"
                >
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700">{perk}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        )}

        {/* Weather Consideration */}
        {plan.weatherConsideration && (
          <div className="card bg-blue-50 border-blue-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Weather & Planning Notes</h2>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <span className="text-gray-700">{plan.weatherConsideration}</span>
            </div>
          </div>
        )}

        {/* Tips */}
        {plan.tips && plan.tips.length > 0 && (
          <div className="card">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Tips & Recommendations</h2>
            <ul className="space-y-3">
              {plan.tips.map((tip, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="flex items-start space-x-3"
                >
                  <Star size={16} className="text-yellow-500 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{tip}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        )}

        {/* Backup Options */}
        {plan.backupOptions && plan.backupOptions.length > 0 && (
          <div className="card">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Backup Options</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {plan.backupOptions.map((option, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <Heart size={16} className="text-red-500" />
                    <span className="font-medium text-gray-900">{option}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            to="/generate"
            className="btn-primary flex-1 text-center"
          >
            Generate Similar Plan
          </Link>
          <button className="btn-secondary flex-1">
            Save Plan
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default PlanDetails; 